// Phase RV-4b — usePendingDrift hook.
//
// Auth-only. Returns the user's most-recent un-decided drift event plus a
// decide() mutator that calls the rollover_drift_decide RPC.
//
// Realtime channel name uses useId() (per-mount), matching the JB-arc /
// useRolloverNotifications cleanup pattern.
//
// N09: never auto-accepts. The caller must explicitly pass a decision.

import { useCallback, useEffect, useId, useMemo, useState } from "react";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { useAuthSession } from "../auth/useAuthSession.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import { useComposedQuery } from "../data/useTableQuery.ts";

export type DriftDecision = "accept" | "replace" | "skip";

export interface DriftEvent {
  event_id: string;
  user_id: string;
  chain_id: string | null;
  slip_id: string | null;
  leg_fixture_id: string;
  leg_market: string;
  leg_pick: string;
  original_odds: number;
  current_odds: number;
  tier_min: number;
  tier_max: number;
  drift_kind: "below_range" | "above_range";
  detected_at: string;
}

export interface DriftReplacementCandidate {
  fixture_id: string;
  match_name: string;
  league: string | null;
  market: string;
  pick: string;
  odds: number | null;
  confidence_score: number | null;
}

export interface UsePendingDriftState {
  pendingEvent: DriftEvent | null;
  replacement: DriftReplacementCandidate | null;
  loading: boolean;
  decideLoading: boolean;
  error: string | null;
  decide: (
    eventId: string,
    decision: DriftDecision,
    replacementFixtureId?: string,
  ) => Promise<void>;
  refetch: () => Promise<void>;
}

function normalise(row: Record<string, unknown>): DriftEvent | null {
  if (typeof row.event_id !== "string") return null;
  if (typeof row.user_id !== "string") return null;
  if (typeof row.leg_fixture_id !== "string") return null;
  if (typeof row.leg_market !== "string") return null;
  if (typeof row.leg_pick !== "string") return null;
  const orig = Number(row.original_odds);
  const curr = Number(row.current_odds);
  const tmin = Number(row.tier_min);
  const tmax = Number(row.tier_max);
  if (![orig, curr, tmin, tmax].every((n) => Number.isFinite(n))) return null;
  const dk = row.drift_kind;
  if (dk !== "below_range" && dk !== "above_range") return null;
  if (typeof row.detected_at !== "string") return null;
  return {
    event_id: row.event_id,
    user_id: row.user_id,
    chain_id: typeof row.chain_id === "string" ? row.chain_id : null,
    slip_id: typeof row.slip_id === "string" ? row.slip_id : null,
    leg_fixture_id: row.leg_fixture_id,
    leg_market: row.leg_market,
    leg_pick: row.leg_pick,
    original_odds: orig,
    current_odds: curr,
    tier_min: tmin,
    tier_max: tmax,
    drift_kind: dk,
    detected_at: row.detected_at,
  };
}

// Composite payload from the sequential read. `error` carries ONLY the
// first-query (drift_events) failure, exactly as the legacy hook did — the
// slip / candidate sub-fetches use optional chaining and never raise.
interface DriftReadResult {
  pendingEvent: DriftEvent | null;
  replacement: DriftReplacementCandidate | null;
  error: string | null;
}

export function usePendingDrift(): UsePendingDriftState {
  const supabase = useMemo(() => getBrowserClient(), []);
  const { user } = useAuthSession();
  const userId = user?.id ?? null;
  const instanceId = useId();
  const [decideLoading, setDecideLoading] = useState(false);
  // Error surfaced by mutations (decide). Read errors come from the query.
  const [mutationError, setMutationError] = useState<string | null>(null);

  const q = useComposedQuery<DriftReadResult>(
    ["pending-drift", userId],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (client) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const sb = client as any;
      const res = await sb
        .from("rollover_drift_events")
        .select(
          "event_id, user_id, chain_id, slip_id, leg_fixture_id, leg_market, leg_pick, original_odds, current_odds, tier_min, tier_max, drift_kind, detected_at",
        )
        .is("user_decision", null)
        .eq("user_id", userId)
        .order("detected_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (res.error) {
        return {
          pendingEvent: null,
          replacement: null,
          error: res.error.message ?? "Failed to load drift events",
        };
      }
      const normalised =
        res.data && typeof res.data === "object"
          ? normalise(res.data as Record<string, unknown>)
          : null;

      // Look up the next-best evaluated replacement candidate for the slip.
      let replacement: DriftReplacementCandidate | null = null;
      if (normalised?.slip_id) {
        const slipRes = await sb
          .from("rollover_slips")
          .select("pipeline_run_id, tier, window_label, selections")
          .eq("slip_id", normalised.slip_id)
          .maybeSingle();
        const slip = slipRes?.data ?? null;
        if (slip?.pipeline_run_id) {
          const excluded = new Set<string>([normalised.leg_fixture_id]);
          if (Array.isArray(slip.selections)) {
            for (const s of slip.selections as Array<Record<string, unknown>>) {
              const fx = typeof s?.fixture_id === "string" ? s.fixture_id : null;
              if (fx) excluded.add(fx);
            }
          }
          const candRes = await sb
            .from("rollover_candidates")
            .select(
              "fixture_id, match_name, league, market, pick, odds, confidence_score",
            )
            .eq("pipeline_run_id", slip.pipeline_run_id)
            .eq("tier", slip.tier)
            .eq("window_label", slip.window_label)
            .eq("pool_tag", "evaluated")
            .order("confidence_score", { ascending: false })
            .limit(20);
          const list = Array.isArray(candRes?.data) ? candRes.data : [];
          const next = list.find(
            (c: { fixture_id: string }) => !excluded.has(c.fixture_id),
          );
          if (next) {
            replacement = {
              fixture_id: String(next.fixture_id),
              match_name: String(next.match_name ?? "TBD vs TBD"),
              league: typeof next.league === "string" ? next.league : null,
              market: String(next.market ?? ""),
              pick: String(next.pick ?? ""),
              odds:
                typeof next.odds === "number"
                  ? next.odds
                  : next.odds !== null && next.odds !== undefined
                  ? Number(next.odds)
                  : null,
              confidence_score:
                typeof next.confidence_score === "number"
                  ? next.confidence_score
                  : next.confidence_score !== null && next.confidence_score !== undefined
                  ? Number(next.confidence_score)
                  : null,
            };
          }
        }
      }

      return { pendingEvent: normalised, replacement, error: null };
    },
    { enabled: Boolean(userId) },
  );

  const refetch = useCallback(async (): Promise<void> => {
    // Parity: legacy refetch() cleared error at the start of the read.
    setMutationError(null);
    await q.refetch();
  }, [q]);

  // Realtime — own-uid filter, per-mount channel.
  useEffect(() => {
    if (!userId) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const sb = supabase as any;
    const channel: RealtimeChannel = sb
      .channel(`rollover_drift_events:${userId}:${instanceId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "rollover_drift_events",
          filter: `user_id=eq.${userId}`,
        },
        () => {
          void q.refetch();
        },
      )
      .subscribe();
    return () => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).removeChannel(channel);
    };
  }, [supabase, userId, q, instanceId]);

  const decide = useCallback(
    async (
      eventId: string,
      decision: DriftDecision,
      replacementFixtureId?: string,
    ): Promise<void> => {
      if (!userId) {
        setMutationError("auth_required");
        return;
      }
      setDecideLoading(true);
      setMutationError(null);
      try {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const sb = supabase as any;
        const res = await sb.rpc("rollover_drift_decide", {
          p_event_id: eventId,
          p_decision: decision,
          p_replacement_fixture_id: replacementFixtureId ?? null,
        });
        if (res.error) {
          setMutationError(res.error.message ?? "decision_failed");
          return;
        }
        await q.refetch();
      } catch (e) {
        setMutationError((e as Error).message);
      } finally {
        setDecideLoading(false);
      }
    },
    [supabase, userId, q],
  );

  const pendingEvent = userId ? q.data?.pendingEvent ?? null : null;
  const replacement = userId ? q.data?.replacement ?? null : null;
  const loading = Boolean(userId) && q.isLoading;
  // Read error (drift_events query) OR the latest mutation error. The query's
  // own thrown errors would surface via q.error, but the read path returns
  // its error inside the payload (legacy parity), so prefer that.
  const error = q.data?.error ?? mutationError ?? (q.error ? q.error.message : null);

  return useMemo<UsePendingDriftState>(
    () => ({
      pendingEvent,
      replacement,
      loading,
      decideLoading,
      error,
      decide,
      refetch,
    }),
    [pendingEvent, replacement, loading, decideLoading, error, decide, refetch],
  );
}
